/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the App component that is the entry point into the react app.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Allows for the creation of side effects within a functional react component.
 */
import { useEffect, useRef, useCallback } from 'react';

/**
 * used to dispatch actions to the redux store.
 */
import { useDispatch } from 'react-redux';

/**
 * Typings & Hook to get access to the react-router history.
 */
import { RouteChildrenProps, useHistory } from 'react-router';

/**
 * Used to handle routing around the react application and displaying different views
 * based on route.
 */
import { Redirect, Route, Switch } from 'react-router-dom';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI Container component.
 */
import { Container } from '@material-ui/core';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Container for search view of application
 */
import Search from './containers/Search';

/**
 * Container for favourites view of application
 */
import Favourites from './containers/Favourites';

/**
 * Container for browse view of application
 */
import BrowseCategories from './containers/BrowseCategories';

/**
 * Container for browse terms view of application
 */
import BrowseTerms from './containers/BrowseTerms';

/**
 * Container for browse grade / subterms view of application
 */
import BrowseSubtermsAndGrades from './containers/BrowseSubtermsAndGrades';

/**
 * Container for browse term information view of application
 */
import BrowseTerm from './containers/BrowseTerm';

/**
 * Container for browse Grade information view of application
 */
import BrowseGrade from './containers/BrowseGrade';

/**
 * The top bar displayed throughout the app.
 */
import AppBar from './components/AppBar';

/**
 * The bottom tab navigation displayed throughout the app.
 */
import Navigation from './components/Navigation';

/**
 * Enables the use of version data loaded from the ServiceStack API.
 */
import { VersionSelectors, useVersionSelector, VersionActions } from './store/reducers/version';

/**
 * Enumeration
 */
import { RequestState } from './store/reducers';

/**
 * Loading indicator.
 */
import Loading from './components/Loading';

/**
 * Allows access to application state.
 */
import { useAppSelector, AppSelectors } from './store/reducers/app';
import Introduction from './containers/Introduction';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface IAppRouteParams {
    versionCode: string;
}

interface IAppProps extends RouteChildrenProps<IAppRouteParams> {

}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(({
    page: {
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    containerWrapper: {
        flexGrow: 1,
        overflow: 'auto',
        position: 'relative'
    },
    container: {
    },
    '@global': {
        '#root': {
            height: '100%'
        },
        body: {
            height: '100%'
        },
        html: {
            height: '100%'
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const App: React.FunctionComponent<IAppProps> = ({
    match
}) => {
    const classes = useStyles();
    // creating the ref by passing initial value null
    const scrollingElementRef = useRef<HTMLDivElement>(null);
    const version = useVersionSelector(VersionSelectors.selectVersion);
    const versionLoadState = useVersionSelector(VersionSelectors.selectVersionLoadState);
    const dispatch = useDispatch();
    const history = useHistory();

    const targetTermId = useAppSelector(AppSelectors.selectTargetTermId);

    if (match == null) {
        return (
            <div>
                error
            </div>
        );
    }

    useEffect(() => {
        dispatch(VersionActions.loadByCode(match.params.versionCode))

        return () => {
            dispatch(VersionActions.clear())
        }
    }, [ match.params.versionCode ])

    useEffect(() => {
        if (versionLoadState.state &&
            versionLoadState.state === RequestState.Failure &&
            versionLoadState.responseStatus &&
            versionLoadState.responseStatus.errorCode === 'NotFound') {
                history.replace('/error/404')
            }
    }, [versionLoadState, version])

    const getScrollElement = useCallback(() => {
        return (scrollingElementRef.current ? scrollingElementRef.current : window) as any;
    }, [scrollingElementRef])

    const SearchComponent = useCallback(({}) => (<Search getScrollElement={getScrollElement} />), [getScrollElement])

    if (!versionLoadState ||
        !versionLoadState.state ||
        versionLoadState.state === RequestState.None ||
        versionLoadState.state === RequestState.Pending) {
            return (
               <Loading />
            );
        }

    if (versionLoadState.state === RequestState.Failure ||
        version === null) {
            return (
                <div>
                    error
                </div>
            );
        }

    return (
        <div
            className={classes.page}
        >
            <AppBar
                title={`CTCAE ${version.name}`}
            />
            <div
                id="scrollContainer"
                className={classes.containerWrapper}
                ref={scrollingElementRef}
            >
                <Container
                    className={classes.container}
                >
                    <Switch>
                        <Redirect
                            path={`${match.url}/`}
                            to={`${match.url}/introduction`}
                            exact
                        />
                        <Route
                            path={`${match.url}/search`}
                            component={SearchComponent}
                        />
                        <Route
                            path={`${match.url}/introduction`}
                            component={Introduction}
                        />
                        <Route
                            path={`${match.url}/favourites`}
                            component={Favourites}
                        />
                        <Route
                            path={`${match.url}/browse/grade/:gradeId`}
                            component={BrowseGrade}
                        />
                        <Route
                            path={`${match.url}/browse/term/:termId/unknown`}
                            component={BrowseTerm}
                        />
                        <Route
                            path={`${match.url}/browse/term/:termId`}
                            component={BrowseSubtermsAndGrades}
                        />
                        <Route
                            path={`${match.url}/browse/category/:categoryId`}
                            component={BrowseTerms}
                        />
                        <Route
                            path={`${match.url}/browse`}
                            component={BrowseCategories}
                        />
                        <Redirect path="/" to="/error/404" />
                    </Switch>
                </Container>
            </div>
            {
                targetTermId ?
                    null :
                    <Navigation
                        versionCode={version.code}
                    />
            }
        </div>
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Export - Default
 * ---------------------------------------------------------------------------------
 */

export default App;


// WEBPACK FOOTER //
// ./src/App.tsx